import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';

// Exponer socket globalmente para debugging (solo en desarrollo)
declare global {
  interface Window {
    __VMS_SOCKET__?: Socket;
  }
}

// Socket.IO necesita HTTP/HTTPS como base, no ws://
// El path por defecto es /socket.io/
const getWSURL = () => {
  const wsUrl = process.env.REACT_APP_WS_URL || 'http://localhost:3009';
  // Si viene con ws://, convertir a http://
  return wsUrl.replace(/^ws:\/\//, 'http://').replace(/^wss:\/\//, 'https://');
};

const WS_URL = getWSURL();

interface StreamStatusEvent {
  cameraId: string;
  status: 'starting' | 'running' | 'stopped' | 'crashed';
  timestamp: string;
}

interface StreamErrorEvent {
  cameraId: string;
  error: string;
  timestamp: string;
}

interface UseStreamWebSocketOptions {
  onStatusChange?: (event: StreamStatusEvent) => void;
  onError?: (event: StreamErrorEvent) => void;
  enabled?: boolean;
}

export const useStreamWebSocket = (options: UseStreamWebSocketOptions = {}) => {
  const { onStatusChange, onError, enabled = true } = options;
  const socketRef = useRef<Socket | null>(null);
  const onStatusChangeRef = useRef(onStatusChange);
  const onErrorRef = useRef(onError);

  // Mantener los refs sincronizados sin recrear el socket
  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const socket = io(WS_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      timeout: 20000,
      forceNew: false,
      autoConnect: true,
    });

    socketRef.current = socket;

    if (process.env.NODE_ENV === 'development') {
      window.__VMS_SOCKET__ = socket;
    }

    socket.on('connect', () => {
      // No loguear conexión normal
    });

    socket.on('disconnect', (reason: string) => {
      if (reason !== 'io client disconnect' && reason !== 'transport close') {
        console.warn('[Socket.IO] Desconexión inesperada:', reason);
      }
      if (reason === 'io server disconnect') {
        socket.connect();
      }
    });

    socket.on('connect_error', (error: Error) => {
      if (!socketRef.current || !socketRef.current.active) {
        return;
      }
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Socket.IO] Error de conexión (reintentando automáticamente):', error.message);
      }
    });

    socket.on('reconnect', (attemptNumber: number) => {
      console.log(`[Socket.IO] Reconectado después de ${attemptNumber} intentos`);
    });

    socket.on('reconnect_attempt', () => {});
    socket.on('reconnect_error', () => {});

    socket.on('reconnect_failed', () => {
      console.error('[Socket.IO] Falló la reconexión después de todos los intentos');
    });

    socket.on('stream-status', (event: StreamStatusEvent) => {
      if (event.status === 'crashed') {
        console.warn(`[Socket.IO] Stream ${event.cameraId} estado: ${event.status}`);
      }
      onStatusChangeRef.current?.(event);
    });

    socket.on('stream-error', (event: StreamErrorEvent) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Socket.IO] Stream error:', event.cameraId, event.error);
      }
      onErrorRef.current?.(event);
    });

    return () => {
      if (socketRef.current) {
        try {
          socketRef.current.removeAllListeners();
          if (socketRef.current.connected) {
            socketRef.current.disconnect();
          }
        } catch {
          // ignore
        }
        socketRef.current = null;
      }
    };
  }, [enabled]);

  return socketRef.current;
};

